import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import {
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

interface AttendanceRecord {
  _id: string;
  employeeId: string;
  month: number;
  year: number;
  workingDays: number;
  paidLeaves: number;
  unpaidLeaves: number;
  createdAt: string;
  updatedAt: string;
}

export default function EmployeeAttendance() {
  const [attendanceRecords, setAttendanceRecords] = useState<
    AttendanceRecord[]
  >([]);
  const [filteredRecords, setFilteredRecords] = useState<AttendanceRecord[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [yearFilter, setYearFilter] = useState<string>("all");

  useEffect(() => {
    fetchAttendance();
  }, []);

  useEffect(() => {
    filterRecords();
  }, [yearFilter, attendanceRecords]);

  const fetchAttendance = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees/me/attendance`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setAttendanceRecords(response.data);
      setFilteredRecords(response.data);
    } catch (error: any) {
      console.error("Error fetching attendance:", error);
      setError(
        error.response?.data?.message || "Failed to fetch attendance records"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const filterRecords = () => {
    if (yearFilter === "all") {
      setFilteredRecords(attendanceRecords);
    } else {
      setFilteredRecords(
        attendanceRecords.filter((r) => r.year.toString() === yearFilter)
      );
    }
  };

  const getMonthName = (month: number) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[month - 1];
  };

  const getUniqueYears = () => {
    const uniqueYearsSet = new Set(attendanceRecords.map((r) => r.year));
    const years = Array.from(uniqueYearsSet);
    return years.sort((a, b) => b - a);
  };

  const calculateTotalStats = () => {
    const stats = filteredRecords.reduce(
      (acc, record) => {
        acc.totalWorkingDays += record.workingDays;
        acc.totalPaidLeaves += record.paidLeaves;
        acc.totalUnpaidLeaves += record.unpaidLeaves;
        return acc;
      },
      {
        totalWorkingDays: 0,
        totalPaidLeaves: 0,
        totalUnpaidLeaves: 0,
      }
    );

    return stats;
  };

  const stats = calculateTotalStats();
  const totalDays =
    stats.totalWorkingDays + stats.totalPaidLeaves + stats.totalUnpaidLeaves;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">
            Loading your attendance records...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Attendance</h1>
          <p className="text-muted-foreground mt-1">
            View your attendance records and statistics
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {attendanceRecords.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Records
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                <span className="text-2xl font-bold">
                  {filteredRecords.length}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Months tracked
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Working Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-2xl font-bold">
                  {stats.totalWorkingDays}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalDays > 0
                  ? `${((stats.totalWorkingDays / totalDays) * 100).toFixed(
                      1
                    )}% of total`
                  : "No data"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Paid Leaves
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <span className="text-2xl font-bold">
                  {stats.totalPaidLeaves}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Days taken</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Unpaid Leaves
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <span className="text-2xl font-bold">
                  {stats.totalUnpaidLeaves}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Days taken</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      {attendanceRecords.length > 0 && (
        <div className="mb-6">
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {getUniqueYears().map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Attendance Records Grid */}
      {filteredRecords.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRecords.map((record) => {
            const totalDaysInMonth =
              record.workingDays + record.paidLeaves + record.unpaidLeaves;
            const attendancePercentage =
              totalDaysInMonth > 0
                ? ((record.workingDays / totalDaysInMonth) * 100).toFixed(1)
                : "0";

            return (
              <Card key={record._id} className="transition-all hover:shadow-lg">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                        <Calendar className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base">
                          {getMonthName(record.month)} {record.year}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                          {attendancePercentage}% Attendance
                        </p>
                      </div>
                    </div>
                    <Badge
                      className={
                        parseFloat(attendancePercentage) >= 90
                          ? "bg-green-100 text-green-800 border-green-200"
                          : parseFloat(attendancePercentage) >= 75
                          ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                          : "bg-red-100 text-red-800 border-red-200"
                      }
                    >
                      {parseFloat(attendancePercentage) >= 90
                        ? "Excellent"
                        : parseFloat(attendancePercentage) >= 75
                        ? "Good"
                        : "Low"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-muted-foreground">
                          Working Days
                        </span>
                      </div>
                      <span className="font-bold text-green-600">
                        {record.workingDays}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-600" />
                        <span className="text-sm text-muted-foreground">
                          Paid Leaves
                        </span>
                      </div>
                      <span className="font-bold text-blue-600">
                        {record.paidLeaves}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-600" />
                        <span className="text-sm text-muted-foreground">
                          Unpaid Leaves
                        </span>
                      </div>
                      <span className="font-bold text-red-600">
                        {record.unpaidLeaves}
                      </span>
                    </div>

                    <div className="pt-3 border-t">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Total Days</span>
                        <span className="font-bold">{totalDaysInMonth}</span>
                      </div>
                    </div>

                    <div className="pt-2">
                      <div className="flex items-center justify-between text-xs mb-2">
                        <span className="text-muted-foreground">
                          Attendance Progress
                        </span>
                        <span className="font-medium">
                          {attendancePercentage}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            parseFloat(attendancePercentage) >= 90
                              ? "bg-green-600"
                              : parseFloat(attendancePercentage) >= 75
                              ? "bg-yellow-600"
                              : "bg-red-600"
                          }`}
                          style={{ width: `${attendancePercentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : attendanceRecords.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Calendar className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">
                No Attendance Records
              </h3>
              <p className="text-muted-foreground mb-4">
                You don't have any attendance records yet.
              </p>
              <p className="text-sm text-muted-foreground">
                Attendance is tracked monthly by the admin.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Calendar className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">
                No Records for {yearFilter}
              </h3>
              <p className="text-muted-foreground mb-4">
                No attendance records found for the selected year.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
